import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AssistantValue } from "../../../services/api/instances/assistantsApi/types";
import type { ChatCompletionsMessageObject } from "../../../services/api/instances/completionsApi/types";
import type {
  DialogValue,
  TDialogs,
} from "../../../services/api/instances/dialogApi/types";
import type { NormalizedStorageObject } from "../../../services/api/instances/storageApi/types/object_types";
import {
  getDialogMessagesReducerBuilder,
  migrationToNewApiReducerBuilder,
  startDialogReducerBuilder,
  startDialogWithCompletionsReducerBuilder,
  uploadFileToDialogReducerBuilder,
} from "./reducers";
import type { DialogsState } from "./types";

const initialState: DialogsState = {
  dialogValue: null,
  selectedAssistant: null,
  messages: [],
  isMessageLoading: false,
  isFileLoading: false,
};

const dialogsSlice = createSlice({
  name: "dialogs",
  initialState,
  reducers: {
    setDialogValue: (state, action: PayloadAction<DialogValue | null>) => {
      state.dialogValue = action.payload;
    },
    setDialogSelectedAssistant: (
      state,
      action: PayloadAction<NormalizedStorageObject<AssistantValue> | null>,
    ) => {
      state.selectedAssistant = action.payload;
    },
    updateDialogField: {
      reducer(state: DialogsState, action: PayloadAction<Partial<TDialogs>>) {
        if (!state.dialogValue?.dialog_object) return;
        Object.assign(state.dialogValue.dialog_object, action.payload);
      },
      prepare(updates: Partial<TDialogs>) {
        return { payload: updates };
      },
    },
    addUserMessage: {
      reducer(
        state: DialogsState,
        action: PayloadAction<ChatCompletionsMessageObject>,
      ) {
        if (!state.dialogValue?.dialog_object) return;
        state.messages.push(action.payload);
      },
      prepare(content: string) {
        return {
          payload: {
            role: "user",
            content,
          },
        };
      },
    },
    clearMessages: (state) => {
      state.messages = [];
    },
  },
  extraReducers: (builder) => {
    startDialogReducerBuilder(builder);
    startDialogWithCompletionsReducerBuilder(builder);
    getDialogMessagesReducerBuilder(builder);
    migrationToNewApiReducerBuilder(builder);
    uploadFileToDialogReducerBuilder(builder);
  },
});

export const dialogsReducer = dialogsSlice.reducer;
export const {
  setDialogValue,
  updateDialogField,
  setDialogSelectedAssistant,
  addUserMessage,
  clearMessages,
} = dialogsSlice.actions;
